import { Injectable } from '@angular/core';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { Subject } from 'rxjs';
import { EBasicField, EBasicFieldShort } from 'src/app/project/shared/enums/basic-fields-enums';
import { compareCustomFieldColumns } from '../../custom-fields/combine-custom-fields/compare-custom-fields';
import { TCurrencyCode } from '../../custom-fields/currencies/currencies.consts';
import { ECustomFieldType } from '../../custom-fields/custom-field-types-enums';
import { TCustomField, TCustomFields } from '../../custom-fields/custom-fields.model';
import { GET_CUSTOM_FIELDS } from '../../custom-fields/custom-fields.store';
import { PreferencesService } from '../../preferences/preferences-service/preferences.service';
import { TWorkspaceLayout } from '../../preferences/preferences.model';
import { SavedViewsService } from '../../saved-views/saved-views.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { TColumn } from './columns/column.model';
import { TableColumnsService } from './columns/table-columns.service';
import { CustomTableService } from './custom-table/custom-table.service';
import { SET_COLLAPSED_GROUPS } from './table.ui.store';

@Injectable({
  providedIn: 'root',
})
export class SiteTableService {
  private readonly _tableLoadChange$ = new Subject<boolean>();
  readonly tableLoadChange$ = this._tableLoadChange$.asObservable();
  private tableLoaded = false;

  constructor(
    private workspaceService: WorkspaceService,
    private customTableService: CustomTableService,
    private preferencesService: PreferencesService,
    private tableColumnsService: TableColumnsService,
    private savedViewsService: SavedViewsService,
  ) {}

  processPreferencesResponse(workspacePreferences: TWorkspaceLayout, workspaceId: string): void {
    const table = this.customTableService.getTable();
    const columns = this.tableColumnsService.getColumns();

    if (workspacePreferences?.columns) {
      SET_COLLAPSED_GROUPS(workspacePreferences.collapsedGroups);

      this.updateColumnData(workspacePreferences, columns);
      this.removeEmptyColumns(workspacePreferences, workspaceId);
      this.checkExistingColumns(columns, workspacePreferences, workspaceId);
      this.removeCustomFieldsFromOtherWorkspaces(workspacePreferences);

      table.updateColumns({
        columns: cloneDeep(workspacePreferences.columns),
        updatePreferences: false,
      });
    } else {
      table.updateColumns();
    }
  }

  private removeCustomFieldsFromOtherWorkspaces(workspacePreferences: TWorkspaceLayout): void {
    const workspace = this.workspaceService.getActiveWorkspace();

    workspacePreferences.columns = workspacePreferences.columns.filter((column) => {
      if (!column.customFieldIds) {
        return true;
      }

      column.customFieldIds = column.customFieldIds.filter(
        (customFieldId) =>
          (!workspace || workspace.customFields.includes(customFieldId)) &&
          GET_CUSTOM_FIELDS()[customFieldId].display,
      );

      return column.customFieldIds.length !== 0;
    });
  }

  private updateColumnData(workspacePreferences: TWorkspaceLayout, columns: TColumn[]): void {
    workspacePreferences.columns = workspacePreferences.columns.filter((column) => {
      if (columns.length === 0) {
        return true;
      }

      if (column.customFieldIds) {
        const existingColumn = columns.find((_column) => {
          let match = false;

          if (_column?.customFieldIds) {
            _column?.customFieldIds.forEach((fieldId) => {
              if (column.customFieldIds.includes(fieldId)) {
                match = true;
              }
            });

            return match;
          } else {
            return column.name === _column.name;
          }
        });

        if (existingColumn) {
          column.currency = existingColumn.currency;
          column.decimalPlaces = existingColumn.decimalPlaces;
          column.unit = existingColumn.unit;
          column.subValuesActive = existingColumn.subValuesActive;
          existingColumn.showTotal = column.showTotal;
          existingColumn.subValuesActive = column.subValuesActive;
          existingColumn.sortIndex = column.sortIndex;
          existingColumn.sortOrder = column.sortOrder;
          existingColumn.groupIndex = column.groupIndex;
          existingColumn.groupOrder = column.groupOrder;

          if (existingColumn.showTotal || typeof existingColumn.showTotal === 'undefined') {
            column.showTotal = true;
          }

          if (
            !existingColumn.subValuesActive ||
            typeof existingColumn.subValuesActive === 'undefined'
          ) {
            column.showTotal = false;
          }

          return true;
        }

        return false;
      } else {
        const existingColumn = columns.find((_column) => column.name === _column.name);

        if (existingColumn) {
          existingColumn.sortIndex = column.sortIndex;
          existingColumn.sortOrder = column.sortOrder;
          existingColumn.groupIndex = column.groupIndex;
          existingColumn.groupOrder = column.groupOrder;

          return true;
        }

        return true;
      }
    });
  }

  private removeEmptyColumns(workspacePreferences: TWorkspaceLayout, workspaceId: string): void {
    workspacePreferences.columns = workspacePreferences.columns.filter((column) => {
      if (!column.customFieldIds) {
        return true;
      } else {
        const customFields = GET_CUSTOM_FIELDS();

        this.filterCustomFieldIds(column, customFields, workspaceId);

        let existingColumns = workspacePreferences.columns.filter(
          (_column) =>
            _column.name === column.name &&
            _column.currency === column.currency &&
            _column.decimalPlaces === column.decimalPlaces &&
            !!_column.showTotal === !!column.showTotal &&
            _column.unit === column.unit &&
            _column.subValuesActive === column.subValuesActive,
        );

        if (column.customFieldIds) {
          existingColumns = existingColumns.filter((_column) =>
            column.customFieldIds.some((customFieldId) =>
              _column.customFieldIds?.includes(customFieldId),
            ),
          );
        }

        if (existingColumns.length > 1) {
          if (!isEqual(existingColumns[0].customFieldIds, column.customFieldIds)) {
            existingColumns[0].customFieldIds = [
              ...existingColumns[0].customFieldIds,
              ...column.customFieldIds,
            ];

            return false;
          }
        }

        if (column.customFieldIds.length === 0) {
          return false;
        }

        return true;
      }
    });
  }

  private filterCustomFieldIds(
    column: TColumn,
    customFields: TCustomFields,
    workspaceId: string,
  ): void {
    column.customFieldIds = column.customFieldIds.filter((customFieldId) => {
      const fieldExists = !!customFields[customFieldId] && customFields[customFieldId]?.display;

      if (!fieldExists) {
        return false;
      }

      const customField = customFields[customFieldId];
      const nameMatches = this.checkNameMatches(customField, column, workspaceId);

      return nameMatches;
    });
  }

  private checkNameMatches(
    customField: TCustomField,
    column: TColumn,
    workspaceId: string,
  ): boolean {
    let nameMatches = true;

    switch (customField.type) {
      case ECustomFieldType.CHECKBOX:
      case ECustomFieldType.DATE:
      case ECustomFieldType.LIST:
      case ECustomFieldType.MULTI_LIST:
      case ECustomFieldType.PERCENTAGE:
      case ECustomFieldType.RICHTEXT:
      case ECustomFieldType.TEXT:
      case ECustomFieldType.TIMELINE:
        nameMatches = customField.label === column.name;
        break;
      case ECustomFieldType.FORMULA: {
        const mockColumn: TColumn = {
          customFieldIds: [customField.id],
        } as Partial<TColumn> as TColumn;

        column.showTotal = customField.showTotal;

        nameMatches = compareCustomFieldColumns(column, mockColumn, workspaceId);
        break;
      }
      case ECustomFieldType.COST: {
        column.currency = customField.currencyCode as TCurrencyCode;
        column.showTotal = customField.showTotal;
        column.subValuesActive = !!customField.subValuesActive;

        nameMatches =
          customField.label === column.name &&
          customField.currencyCode === column.currency &&
          customField.showTotal === column.showTotal &&
          !!customField.subValuesActive === !!column.subValuesActive;

        break;
      }
      case ECustomFieldType.NUMBERS:
        column.decimalPlaces = +customField.decimalPlaces;
        column.unit = customField.unit;
        column.showTotal = customField.showTotal;
        !!customField.subValuesActive === !!column.subValuesActive;

        nameMatches =
          customField.label === column.name &&
          customField.unit === column.unit &&
          customField.decimalPlaces === column.decimalPlaces &&
          customField.showTotal === column.showTotal &&
          !!customField.subValuesActive === !!column.subValuesActive;
        break;
      case ECustomFieldType.TIME:
        column.showTotal = customField.showTotal;

        nameMatches =
          customField.label === column.name && customField.showTotal === column.showTotal;
        break;
    }
    return nameMatches;
  }

  private checkExistingColumns(
    columns: TColumn[],
    workspacePreferences: TWorkspaceLayout,
    workspaceId: string,
  ): void {
    this.checkIfColumnNeedsCustomFields(workspacePreferences);

    columns.forEach((column) => {
      if (column.customFieldIds) {
        column.customFieldIds = column.customFieldIds.filter((customFieldId) => {
          const customField = GET_CUSTOM_FIELDS()[customFieldId];

          if (!customField) {
            return false;
          }

          return true;
        });

        const existingColumn = workspacePreferences.columns.find((_column) =>
          compareCustomFieldColumns(column, _column, workspaceId),
        );

        if (!existingColumn) {
          const columnToMerge = workspacePreferences.columns.find(
            (_column) =>
              _column.name === column.name &&
              _column.currency === column.currency &&
              _column.unit === column.unit &&
              _column.subValuesActive === column.subValuesActive,
          );

          if (columnToMerge) {
            column.customFieldIds.forEach((fieldId) => {
              if (!columnToMerge.customFieldIds.includes(fieldId)) {
                columnToMerge.customFieldIds.push(fieldId);
              }
            });
          } else {
            workspacePreferences.columns.push(column);
          }
        } else {
          if (!existingColumn.customFieldIds.includes(column.customFieldIds[0])) {
            existingColumn.customFieldIds.push(column.customFieldIds[0]);
          }
        }
      }
    });
  }

  private checkIfColumnNeedsCustomFields(workspacePreferences: TWorkspaceLayout): void {
    workspacePreferences.columns.forEach((column) => {
      switch (column.name) {
        case EBasicField.ACCOUNT:
        case EBasicField.SITE:
        case EBasicField.PRIORITY:
        case EBasicFieldShort.PRIORITY:
        case EBasicField.STATUS:
        case EBasicFieldShort.STATUS:
        case EBasicField.SEQUENCE_NUMBER:
        case EBasicFieldShort.SEQUENCE_NUMBER:
        case EBasicField.TITLE:
        case EBasicField.DESCRIPTION:
        case EBasicField.CREATED:
        case EBasicField.UPDATED:
        case EBasicField.CREATED_BY:
        case EBasicField.ASSIGNEES:
        case EBasicField.TAGS:
        case EBasicField.FLAGGED:
        case EBasicFieldShort.FLAGGED:
          break;
        default:
          if (!column.customFieldIds) {
            column.customFieldIds = [];
          }
          break;
      }
    });
  }

  setTableLoaded(loaded: boolean): void {
    this._tableLoadChange$.next(loaded);
    this.tableLoaded = loaded;
  }

  getTableLoaded(): boolean {
    return this.tableLoaded;
  }
}
